import {
  AltiusRes,
  IGetLatestValuesQuery,
  IGetQuickViewData,
  IGetQuickViewSettings,
  ILatestValuesResponse,
  IQuickViewCreateNewItem,
  QuickViewMeasureData,
  QuickViewMeta,
  QuickViewQueries,
  // UserLight
  QuickViewTableData
} from "store/types";
import downloadFileAxios from "utils/download-file-axios";

import { ReportsAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends (...args: any) => any
      ? T[P] | undefined
      : T[P] extends object
        ? RecursivePartial<T[P]>
        : T[P];
};

export interface IQuickViewActions {
  getQuickViewTableAction: (data: IGetQuickViewData) => Promise<QuickViewTableData>;
  getQuickViewSettingsAction: () => Promise<IGetQuickViewSettings>;
  getMeasureLatestValues: (query: IGetLatestValuesQuery) => Promise<QuickViewMeasureData[]>;
  getQuickViewMetadata: () => Promise<void>;
  createQuickViewLineAction: (data: IQuickViewCreateNewItem) => Promise<void>;
  exportQuickViewTableAction: (data: IGetQuickViewData) => Promise<void>;
  setQuickViewQueriesAction: (data: { queries: RecursivePartial<QuickViewQueries>; replace?: boolean }) => void;
}

export class QuickViewActions extends AsyncActions implements IQuickViewActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getQuickViewTableAction = async ({
    measure_filters,
    filters,
    ...rest
  }: IGetQuickViewData): Promise<QuickViewTableData> => {
    try {
      const {
        data: { data }
      } = await this.api.post<AltiusRes<QuickViewTableData>>({
        url: "/api/v0.0.3/reports/deal-quick-view",
        data: {
          ...rest,
          filters: {
            ...filters,
            ...(measure_filters && Object.keys(measure_filters).length && { measures: measure_filters })
          }
        }
      });

      return data;
    } catch (error) {
      throw error;
    }
  };

  public getQuickViewSettingsAction = async (): Promise<IGetQuickViewSettings> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<IGetQuickViewSettings>>({
      url: "/api/v0.0.3/reports/deal-quick-view/settings"
    });
    return payload;
  };

  public getMeasureLatestValues = async (query: IGetLatestValuesQuery): Promise<QuickViewMeasureData[]> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<ILatestValuesResponse[]>>({
      url: "/api/v0.0.3/reports/deal-quick-view/measure-values",
      params: query
    });
    const response: QuickViewMeasureData[] = payload.map((item) => {
      return {
        ...item,
        value: Number(item.value.replace(/,/g, ""))
      };
    });
    return response;
  };

  public getQuickViewMetadata = async (): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<QuickViewMeta>>({
      url: "/api/v0.0.3/reports/quick-view-metadata"
    });
    this.dispatch({
      type: ReportsAction.SetQuickViewMeta,
      payload
    });
  };

  public createQuickViewLineAction = async (data: IQuickViewCreateNewItem) => {
    await this.api.post({
      url: "/api/v0.0.3/reports/deal-quick-view/add-new-line",
      data: data
    });
  };

  public exportQuickViewTableAction = async ({
    per_page = 50,
    measure_filters,
    filters,
    ...rest
  }: IGetQuickViewData): Promise<void> => {
    await this.api
      .post({
        url: "/api/v0.0.3/reports/deal-quick-view/export",
        data: {
          per_page,
          ...rest,
          filters: {
            ...filters,
            ...(measure_filters && Object.keys(measure_filters).length && { measures: measure_filters })
          }
        },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public setQuickViewQueriesAction: IQuickViewActions["setQuickViewQueriesAction"] = (payload) => {
    this.dispatch({ type: ReportsAction.SetQuickViewQueries, payload });
  };
}
